/**
 * modoAcademiaSim.js — el simulador de autoescuelas en modo «Academia» (3/11/2026, Eva, academia de apoyo escolar).
 * Lead con sector «Academia» → simulador de autoescuela sin vehículos (Flota, Exámenes, Tests, Cursos y Fichajes
 * ocultos) y con el vocabulario de clases: «práctica» → «clase», «autoescuela» → «academia». El cambio de palabras
 * se hace sobre el texto que se pinta (VocabularioAcademia), sin tocar las pantallas de autoescuela.
 */
import { useEffect, useSyncExternalStore } from "react";
import { setSeccionesOcultas, resetSeccionesOcultas } from "./seccionesStore";

export const ACADEMIA_MODULOS_OCULTOS = ["examenes", "tests", "cursos", "flota", "fichajes"];

let _on = false;
const _subs = new Set();
const emit = () => _subs.forEach((fn) => { try { fn(_on); } catch { /* nada */ } });

export const esModoAcademia = () => _on;
export function useModoAcademia() {
  return useSyncExternalStore((fn) => { _subs.add(fn); return () => _subs.delete(fn); }, esModoAcademia, esModoAcademia);
}

/** ¿El sector del lead es una academia? (la etiqueta libre de ventas: «Academia», «academia de apoyo escolar»…) PURA. */
export const esSectorAcademia = (raw) => /academ/i.test(String(raw || ""));

export function entrarModoAcademiaSim(on) {
  _on = !!on;
  if (_on) setSeccionesOcultas(ACADEMIA_MODULOS_OCULTOS);
  else resetSeccionesOcultas();
  emit();
}

// Orden: plurales y mayúsculas antes que las formas cortas. Sin \b: la «á» rompe los límites de palabra de JS.
const CAMBIOS = [
  ["AUTOESCUELAS", "ACADEMIAS"], ["Autoescuelas", "Academias"], ["autoescuelas", "academias"],
  ["AUTOESCUELA", "ACADEMIA"], ["Autoescuela", "Academia"], ["autoescuela", "academia"],
  ["Autoescola", "Acadèmia"], ["autoescola", "acadèmia"],
  ["PRÁCTICAS", "CLASES"], ["Prácticas", "Clases"], ["prácticas", "clases"],
  ["PRÁCTICA", "CLASE"], ["Práctica", "Clase"], ["práctica", "clase"],
  ["el carnet", "el curso"], ["carnet", "curso"],
  // Sin coches: el «vehículo» de una academia es el aula; los tipos de clase de fábrica, asignaturas.
  ["VEHÍCULOS", "AULAS"], ["Vehículos", "Aulas"], ["vehículos", "aulas"], ["Vehículo", "Aula"], ["vehículo", "aula"],
  ["COCHES", "AULAS"], ["Coches", "Aulas"], ["coches", "aulas"], ["COCHE", "AULA"], ["Coche", "Aula"], ["coche", "aula"],
  ["Flota", "Aulas"], ["Matrícula del aula", "Aula"],
  // Eva (3/11/2026): clases sobre todo en grupo y a veces individuales → los tipos dicen si es grupo o individual.
  ["Clase teórica", "Grupo · Repaso general"], ["Ciudad", "Grupo · Matemáticas"], ["Carretera", "Grupo · Lengua"],
  ["Autovía", "Individual · Inglés"], ["Simular clase", "Probar un aviso"],
  // Panel, Alumnos, Profesores y Cobros: nada de DGT ni de permisos de conducir.
  ["Examen DGT", "Examen"], ["Tasas DGT", "Material"], ["Tasa DGT", "Material"], ["tasa DGT", "material"],
  ["Sin tasa", "Sin material"], ["Nº de tasa", "Nº de recibo"], ["DGT", "academia"],
  ["CAP inicial", "Curso intensivo"], ["CAP", "Intensivo"], ["ITV", "Revisión"],
  ["Teórica", "Teoría"], ["teórica", "teoría"], ["Permisos", "Cursos"], ["permisos", "cursos"],
  ["Permiso", "Curso"], ["permiso", "curso"], ["Carnet", "Curso"], ["de conducir", "de la academia"], ["conducir", "estudiar"],
];

/** Texto con el vocabulario de academia. PURA. */
export function aVocabularioAcademia(texto) {
  let t = String(texto);
  for (const [a, b] of CAMBIOS) if (t.includes(a)) t = t.split(a).join(b);
  return t;
}

function cambiaNodo(n) {
  if (!n || n.nodeType !== 3) return;
  const v = n.nodeValue;
  if (!v || v.length < 4) return;
  const w = aVocabularioAcademia(v);
  if (w !== v) n.nodeValue = w;
}

function recorre(root) {
  if (!root) return;
  if (root.nodeType === 3) { cambiaNodo(root); return; }
  const it = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let n = it.nextNode();
  while (n) { cambiaNodo(n); n = it.nextNode(); }
  if (root.querySelectorAll) {
    root.querySelectorAll("input[placeholder],textarea[placeholder]").forEach((el) => {
      const p = el.getAttribute("placeholder");
      const q = aVocabularioAcademia(p);
      if (q !== p) el.setAttribute("placeholder", q);
    });
  }
}

/** Montado en la raíz del simulador: con el modo Academia encendido, reescribe el texto que se pinta. */
export function VocabularioAcademia() {
  const on = useModoAcademia();
  useEffect(() => {
    if (!on || typeof MutationObserver === "undefined") return undefined;
    recorre(document.body);
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData") cambiaNodo(m.target);
        else m.addedNodes.forEach((n) => recorre(n));
      }
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [on]);
  return null;
}
